import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

export const baseURL = import.meta.env.VITE_API_BASE_URL ?? '';

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

export function getCookie(name: string): string | undefined {
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.split('=')[1];
}

// Paths that must be prefixed with /api/orgs/:orgSlug — excludes auth and
// already-scoped paths.
const ORG_RESOURCE_RE = /^\/api\/(jobs|customers|products|quotes|invoices|scheduling|api-keys|invites)(\/.*)?$/;

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const method = config.method?.toUpperCase();

  if (method && ['POST', 'PATCH', 'PUT', 'DELETE'].includes(method)) {
    const csrfToken = getCookie('csrf_token');
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }

  if (config.url && ORG_RESOURCE_RE.test(config.url)) {
    const orgSlug = localStorage.getItem('currentOrgSlug');
    if (!orgSlug) {
      return Promise.reject(new Error('No org selected. Cannot make org-scoped request.'));
    }
    config.url = `/api/orgs/${encodeURIComponent(orgSlug)}${config.url.replace('/api', '')}`;
  }

  return config;
});

export let refreshPromise: Promise<string | null> | null = null;

type RetriableRequest = InternalAxiosRequestConfig & { _retry?: boolean };

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as RetriableRequest | undefined;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!refreshPromise) {
        refreshPromise = axios
          .post(`${baseURL}/api/auth/refresh`, {}, { withCredentials: true })
          .then(() => null)
          .finally(() => {
            refreshPromise = null;
          });
      }

      try {
        await refreshPromise;
        return apiClient(originalRequest);
      } catch (refreshError) {
        window.location.replace('/login');
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

const MAX_SAFE_ERROR_LENGTH = 80;
const BLOCKED_ERROR_SUBSTRINGS = [
  'SQL',
  'database',
  'stack',
  'undefined',
  'null',
  'Error:',
  'TypeError',
  'at ',
] as const;

export function sanitiseApiError(raw: string, fallback: string): string {
  if (
    raw.length <= MAX_SAFE_ERROR_LENGTH &&
    !BLOCKED_ERROR_SUBSTRINGS.some((blocked) => raw.includes(blocked))
  ) {
    return raw;
  }

  return fallback;
}

export function extractApiError(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { error?: string } } }).response;
    if (response?.data?.error) {
      return sanitiseApiError(response.data.error, fallback);
    }
  }

  return fallback;
}
